/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  StopOutlined,
  WarningOutlined,
} from '@ant-design/icons'
import type { ReactNode } from 'react'
import colors from '@sportninja/common/constants/appColors'
import type { TeamStanding } from '@sportninja/common/features/team-balance/utils/teamStanding'
import type {
  TeamPaymentStatus,
  TeamPaymentStatusCode,
} from '@sportninja/common/services/teamBalance'
import { paragraphXSmall, weightSemiBold } from 'src/components/css'
import { media } from 'src/components/Responsive'
import Text from 'src/components/ts-components/Text'
import type { HeaderMoney } from '../utils/balanceTax'
import { clockCopy, standingSentence } from '../utils/standingCopy'
import type { StandingFormatters } from '../utils/standingCopy'
import TeamPaymentStatusBadge, { PRESENTATION } from './TeamPaymentStatusBadge'
import TeamPaymentStatusLegend from './TeamPaymentStatusLegend'

/** The phone breakpoint (≤863px). `Responsive` is untyped JS, hence the cast. */
const MOBILE = (media as Record<string, string>).mobile

/**
 * The banner's icon per status. Qualified — Balance Due gets the check even
 * though its pill has no glyph: the sentence leads with the spot being secured
 * (Inon, Sep 22: green once the team qualifies).
 */
const ICONS: Record<TeamPaymentStatusCode, ReactNode> = {
  minimum_payment_due: <ClockCircleOutlined aria-hidden='true' />,
  qualified_balance_due: <CheckCircleOutlined aria-hidden='true' />,
  not_qualified: <StopOutlined aria-hidden='true' />,
  balance_overdue: <WarningOutlined aria-hidden='true' />,
  paid_in_full: <CheckCircleOutlined aria-hidden='true' />,
}

type TeamStandingHeaderProps = {
  standing: TeamStanding
  /** The raw server status, for the pill beside the sentence. */
  status: TeamPaymentStatus | null | undefined
  money: HeaderMoney
  /** The competition's name ("Fall 2026 League") — what the minimum secures a spot in. */
  league: string | null
  fmt: StandingFormatters
}

/**
 * SN-8310 (Jeff and James, Sep 22; design C4) — where the team stands, in one
 * sentence: how much, by when, and what for.
 *
 * Jeff: "it should be clear at any point in the season that they're either in
 * good standing, or they're outstanding, or if they really go into arrears."
 * The runway made the rep read two bars, an axis and a pill and do the maths;
 * this says the answer. The milestone track below it (`PaymentMilestones`)
 * carries the dates.
 *
 * Tinted in the pill's own colour, so the banner and the pill can never tell
 * two stories — and both come from the server's status code.
 *
 * Inon, Sep 24: ONE sentence. Its old second line restated the minimum, the
 * amount paid, the fee and the dates, all of which the milestones and the
 * figures row already show; the clock is the chip's alone. Without a status
 * there is no banner at all (`BalanceTab`): the figures row says it all.
 */
const TeamStandingHeader = ({
  standing,
  status,
  money,
  league,
  fmt,
}: TeamStandingHeaderProps) => {
  const color = PRESENTATION[standing.code].color
  const clock = clockCopy(standing, fmt)

  return (
    <div
      css={[bannerStyle, tintStyle(color)]}
      data-testid='team-standing-banner'
      data-tier={standing.tier}
    >
      <span css={iconStyle} style={{ color }}>
        {ICONS[standing.code]}
      </span>
      <div css={bodyStyle} data-testid='team-standing-sentence'>
        <Text
          variant='paragraphLarge'
          weight='semiBold'
          color={colors.NEUTRAL_0}
        >
          {standingSentence(standing, money, league, fmt)}
        </Text>
      </div>
      <div css={sideStyle}>
        <div css={statusRowStyle}>
          <TeamPaymentStatusBadge status={status} />
          <TeamPaymentStatusLegend
            standing={standing}
            money={money}
            fmt={fmt}
          />
        </div>
        {clock ? (
          <span
            css={[chipStyle, clock.overdue ? overdueChipStyle : undefined]}
            data-testid='team-standing-countdown'
            data-overdue={clock.overdue}
          >
            {clock.text}
          </span>
        ) : null}
      </div>
    </div>
  )
}

export default TeamStandingHeader

const bannerStyle = css`
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: start;
  gap: 12px 16px;
  padding: 16px;
  border-radius: 8px;
  border: 1px solid;

  ${MOBILE} {
    grid-template-columns: auto minmax(0, 1fr);
  }
`

/** The status colour at 15% for the surface and 40% for the edge. */
const tintStyle = (color: string) => css`
  background: ${color}26;
  border-color: ${color}66;
`

const iconStyle = css`
  display: flex;
  font-size: 24px;
  line-height: 1;
  padding-top: 2px;
`

const bodyStyle = css`
  min-width: 0;
`

/** Pill, legend and countdown; stacked right, and in a wrapping row on a phone. */
const sideStyle = css`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 8px;

  ${MOBILE} {
    grid-column: 1 / -1;
    grid-row: 1;
    flex-direction: row;
    flex-wrap: wrap;
    align-items: center;
  }
`

const statusRowStyle = css`
  display: flex;
  align-items: center;
  gap: 8px;
`

const chipStyle = css`
  ${paragraphXSmall};
  ${weightSemiBold};
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 8px;
  background: ${colors.SECONDARY_200};
  color: ${colors.NEUTRAL_0};
  white-space: nowrap;
`

/** Days overdue, in the past-due red the pill beside it uses. */
const overdueChipStyle = css`
  color: ${colors.ERROR_100};
`
